import { useEffect } from 'react';
import './Legal.css';

const LAST_UPDATED = 'April 21, 2026';
const CONTACT_EMAIL = 'support@d33p.ai';

function LegalLayout({ title, eyebrow, children, onNavigate }) {
    useEffect(() => { window.scrollTo(0, 0); }, []);
    return (
        <div className="legalPage">
            <header className="legalNav">
                <span className="legalLogo" onClick={() => onNavigate('landing')}>D33P</span>
                <button className="legalBackBtn" onClick={() => onNavigate('landing')}>← Back</button>
            </header>

            <main className="legalBody">
                <span className="legalEyebrow">{eyebrow}</span>
                <h1 className="legalTitle">{title}</h1>
                <div className="legalMeta">Last updated · {LAST_UPDATED}</div>
                <div className="legalContent">{children}</div>
            </main>

            <footer className="legalFooter">
                <div className="legalFooterInner">
                    <span className="legalLogo">D33P</span>
                    <div className="legalFooterLinks">
                        <button onClick={() => onNavigate('terms')}>Terms</button>
                        <button onClick={() => onNavigate('privacy')}>Privacy</button>
                        <button onClick={() => onNavigate('compliance')}>2257</button>
                        <button onClick={() => onNavigate('contact')}>Contact</button>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export function Terms({ onNavigate }) {
    return (
        <LegalLayout title="Terms of Service" eyebrow="Legal" onNavigate={onNavigate}>
            <section>
                <p className="legalIntro">
                    Welcome to D33P. By accessing or using our platform, you agree to be bound by these Terms of Service.
                    If you do not agree, you must not use the service.
                </p>
            </section>

            <section>
                <h2>1. Age Requirement</h2>
                <p>
                    You must be at least <strong>18 years of age</strong> (or the age of majority in your jurisdiction, whichever is greater)
                    to use D33P. By using the service you represent and warrant that you meet this requirement.
                    We do not knowingly allow minors to access the platform. If we discover a minor is using the service,
                    their account will be terminated immediately.
                </p>
            </section>

            <section>
                <h2>2. Description of Service</h2>
                <p>
                    D33P provides an AI-driven platform that generates synthetic adult photographs and videos based on
                    user-selected parameters (pose, clothing, location, character attributes). All generated content is
                    produced by machine-learning models and does not depict real persons.
                </p>
            </section>

            <section>
                <h2>3. Acceptable Use</h2>
                <p>You agree NOT to use D33P to generate, store, distribute, or attempt to generate content that:</p>
                <ul>
                    <li>Depicts or appears to depict minors in any sexual or suggestive context</li>
                    <li>Resembles or impersonates any real, identifiable person without their explicit prior written consent</li>
                    <li>Promotes non-consensual acts, violence, or illegal activity</li>
                    <li>Violates any applicable local, national, or international law</li>
                    <li>Harasses, threatens, or defames others</li>
                </ul>
                <p>
                    Violation of this section results in immediate account termination, forfeiture of remaining tokens,
                    and may be reported to law enforcement.
                </p>
            </section>

            <section>
                <h2>4. Payments, Tokens, and Refunds</h2>
                <p>
                    D33P operates on a one-time token-purchase model. Prices are displayed in USD at checkout.
                    Payments are processed by a third-party provider (Finby); we do not store your payment card details.
                </p>
                <p>
                    Tokens are non-transferable and have no cash value. Due to the digital and consumable nature of the service,
                    <strong> all sales are final and non-refundable</strong>, except where required by law. If your payment fails
                    after tokens were delivered, we reserve the right to revoke those tokens.
                </p>
            </section>

            <section>
                <h2>5. User Content & Ownership</h2>
                <p>
                    Subject to full compliance with these Terms, you retain ownership of content you generate through the service
                    and may use it for personal, non-commercial purposes. You grant D33P a non-exclusive, royalty-free license
                    to process, store, and deliver your generations to operate and improve the service.
                </p>
                <p>
                    You are solely responsible for how you use or distribute generated content outside the platform.
                </p>
            </section>

            <section>
                <h2>6. Intellectual Property</h2>
                <p>
                    The D33P name, logo, interface, source code, and underlying models are owned by us or our licensors and
                    are protected by copyright, trademark, and other laws. You may not copy, modify, reverse-engineer, or create
                    derivative works of the platform.
                </p>
            </section>

            <section>
                <h2>7. Account Termination</h2>
                <p>
                    We may suspend or terminate your account at any time, without notice, for any violation of these Terms,
                    suspected fraud, chargebacks, or abuse of the system. Upon termination, your access to generated content
                    and remaining tokens may be revoked.
                </p>
            </section>

            <section>
                <h2>8. Disclaimer of Warranties</h2>
                <p>
                    The service is provided <strong>"as is"</strong> and <strong>"as available"</strong>, without warranties of
                    any kind. We do not guarantee that generations will meet your expectations, that the service will be
                    uninterrupted, or that content will be free of errors or artifacts.
                </p>
            </section>

            <section>
                <h2>9. Limitation of Liability</h2>
                <p>
                    To the maximum extent permitted by law, D33P and its operators shall not be liable for any indirect,
                    incidental, special, consequential, or punitive damages arising from or related to your use of the service.
                    Our total aggregate liability shall not exceed the amount you paid us in the twelve months preceding the claim.
                </p>
            </section>

            <section>
                <h2>10. Changes to These Terms</h2>
                <p>
                    We may update these Terms from time to time. Material changes will be reflected in the "Last updated" date
                    at the top of this page. Continued use of the service after changes are posted constitutes acceptance of
                    the revised Terms.
                </p>
            </section>

            <section>
                <h2>11. Governing Law</h2>
                <p>
                    These Terms are governed by the laws of the jurisdiction where D33P is registered, without regard to
                    conflict-of-law principles. Any disputes shall be resolved in the competent courts of that jurisdiction.
                </p>
            </section>

            <section>
                <h2>12. Contact</h2>
                <p>
                    Questions about these Terms? Reach us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Privacy({ onNavigate }) {
    return (
        <LegalLayout title="Privacy Policy" eyebrow="Legal" onNavigate={onNavigate}>
            <section>
                <p className="legalIntro">
                    This Privacy Policy explains what information D33P collects, how we use it, and the choices you have.
                    We take your privacy seriously — especially given the nature of the content our service produces.
                </p>
            </section>

            <section>
                <h2>1. Information We Collect</h2>
                <p><strong>Account data.</strong> Email address (via direct sign-up or Google OAuth), authentication tokens,
                account creation timestamp.</p>
                <p><strong>Usage data.</strong> Generation parameters (pose, outfit, etc.), generated media URLs linked to your
                account, token balance, and a log of generation requests.</p>
                <p><strong>Payment data.</strong> Handled entirely by our payment processor (Finby). We store only a transaction
                reference and the purchased tier — never card numbers, CVVs, or billing addresses.</p>
                <p><strong>Technical data.</strong> IP address, user agent, browser type, and basic device information for
                fraud prevention and debugging.</p>
            </section>

            <section>
                <h2>2. How We Use Your Information</h2>
                <ul>
                    <li>To operate the service (authenticate, process generations, deliver results)</li>
                    <li>To process payments and allocate tokens</li>
                    <li>To prevent fraud, abuse, and misuse</li>
                    <li>To improve the quality of our AI models (in aggregated, non-identifiable form)</li>
                    <li>To communicate service-related updates</li>
                </ul>
            </section>

            <section>
                <h2>3. Third-Party Services</h2>
                <p>We share limited data with third parties strictly to operate the service:</p>
                <ul>
                    <li><strong>Finby</strong> — payment processing</li>
                    <li><strong>Google OAuth</strong> — optional sign-in (name and email only)</li>
                    <li><strong>Cloud infrastructure</strong> (DigitalOcean Spaces) — media storage</li>
                    <li><strong>GPU providers</strong> (RunPod or equivalent) — running generation models</li>
                </ul>
                <p>We do not sell your personal data to third parties. Ever.</p>
            </section>

            <section>
                <h2>4. Generated Content</h2>
                <p>
                    Your generated media is stored on encrypted cloud storage and accessible only via tokenized URLs.
                    We do not share your gallery with other users. Generations are kept as long as your account is active;
                    when you delete your account, generations are deleted within 30 days.
                </p>
            </section>

            <section>
                <h2>5. Cookies & Local Storage</h2>
                <p>
                    We use local storage to keep you signed in (authentication token) and to track small UX state
                    (like the tap-game set rotator). We do not use advertising cookies or third-party tracking pixels.
                </p>
            </section>

            <section>
                <h2>6. Data Retention</h2>
                <p>
                    Account data is retained for as long as your account is active. Upon deletion request, we remove personal data
                    within 30 days, except where retention is required by law (e.g. payment records for tax purposes).
                </p>
            </section>

            <section>
                <h2>7. Your Rights</h2>
                <p>Depending on your jurisdiction (GDPR, CCPA, etc.), you may have the right to:</p>
                <ul>
                    <li>Access a copy of your personal data</li>
                    <li>Request correction or deletion</li>
                    <li>Object to certain processing</li>
                    <li>Data portability</li>
                </ul>
                <p>
                    To exercise these rights, email us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    We respond within 30 days.
                </p>
            </section>

            <section>
                <h2>8. Security</h2>
                <p>
                    We use TLS for all network traffic, hashed and salted password storage, and industry-standard access controls.
                    No system is 100% secure — if we ever experience a breach affecting you, we'll notify you promptly.
                </p>
            </section>

            <section>
                <h2>9. Children</h2>
                <p>
                    D33P is strictly for adults. We do not knowingly collect information from anyone under 18. If you
                    believe a minor has created an account, email us immediately and we will remove it.
                </p>
            </section>

            <section>
                <h2>10. Changes to This Policy</h2>
                <p>
                    We'll update the "Last updated" date when this policy changes. For material changes, we'll notify signed-in
                    users by email.
                </p>
            </section>

            <section>
                <h2>11. Contact</h2>
                <p>
                    Privacy questions? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Compliance2257({ onNavigate }) {
    return (
        <LegalLayout title="18 U.S.C. § 2257 Compliance Statement" eyebrow="Compliance" onNavigate={onNavigate}>
            <section>
                <p className="legalIntro">
                    This statement addresses the applicability of the record-keeping requirements under
                    18 U.S.C. § 2257 and 28 C.F.R. Part 75 to content distributed by D33P.
                </p>
            </section>

            <section>
                <h2>AI-Generated Content Only</h2>
                <p>
                    All visual content produced and made available by D33P is <strong>entirely generated by
                    artificial-intelligence models</strong>. No content on this platform depicts real human beings,
                    actual sexual conduct, or any actual performers.
                </p>
                <p>
                    Because 18 U.S.C. § 2257 applies to depictions of <em>actual</em> sexually explicit conduct
                    performed by <em>actual</em> human beings, and because no such persons or conduct exist in our
                    synthetic content, the record-keeping obligations under § 2257 and 28 C.F.R. Part 75 are
                    not applicable to D33P.
                </p>
            </section>

            <section>
                <h2>No Real Persons Depicted</h2>
                <p>
                    D33P strictly prohibits the generation, upload, or distribution of content that resembles,
                    impersonates, or attempts to recreate any real identifiable individual. Any content reported as
                    depicting a real person will be removed, and the responsible account will be terminated.
                </p>
            </section>

            <section>
                <h2>Prohibition on Depictions of Minors</h2>
                <p>
                    D33P maintains zero tolerance for any content that depicts or appears to depict minors in
                    any sexual context. Our models, policies, and monitoring systems work to detect and block such
                    content. Any attempt to produce such material will result in immediate account termination and
                    may be reported to the National Center for Missing & Exploited Children (NCMEC) and law enforcement.
                </p>
            </section>

            <section>
                <h2>Designated Contact</h2>
                <p>
                    Questions about content on D33P, or reports of suspected misuse, should be sent to
                    <a href={`mailto:${CONTACT_EMAIL}`}> {CONTACT_EMAIL}</a>. Reports will be reviewed within 24 hours.
                </p>
            </section>

            <section>
                <h2>Disclaimer</h2>
                <p>
                    This statement is provided for informational purposes only and does not constitute legal advice.
                    Operators and users should consult qualified legal counsel regarding applicable laws in their
                    jurisdiction.
                </p>
            </section>
        </LegalLayout>
    );
}

export function Contact({ onNavigate }) {
    return (
        <LegalLayout title="Get in touch" eyebrow="Contact" onNavigate={onNavigate}>
            <section>
                <p className="legalIntro">
                    Whether you've got a question, found a bug, have a partnership idea, or need help with your account —
                    we're real humans and we read every message.
                </p>
            </section>

            <div className="contactGrid">
                <a href={`mailto:${CONTACT_EMAIL}`} className="contactCard">
                    <div className="contactCardIcon">✉</div>
                    <div className="contactCardTitle">General & Support</div>
                    <div className="contactCardValue">{CONTACT_EMAIL}</div>
                    <div className="contactCardDesc">Account issues, bug reports, feature requests. Response within 24h.</div>
                </a>

                <a href="mailto:abuse@d33p.ai" className="contactCard">
                    <div className="contactCardIcon">!</div>
                    <div className="contactCardTitle">Abuse & Takedown</div>
                    <div className="contactCardValue">abuse@d33p.ai</div>
                    <div className="contactCardDesc">Report content, copyright claims, impersonation. Reviewed within 24h.</div>
                </a>

                <a href="mailto:press@d33p.ai" className="contactCard">
                    <div className="contactCardIcon">◆</div>
                    <div className="contactCardTitle">Press & Partnerships</div>
                    <div className="contactCardValue">press@d33p.ai</div>
                    <div className="contactCardDesc">Media, investors, business collaborations.</div>
                </a>

                <a href="mailto:legal@d33p.ai" className="contactCard">
                    <div className="contactCardIcon">§</div>
                    <div className="contactCardTitle">Legal</div>
                    <div className="contactCardValue">legal@d33p.ai</div>
                    <div className="contactCardDesc">DMCA, subpoenas, legal notices, compliance questions.</div>
                </a>
            </div>

            <section>
                <h2>Response times</h2>
                <p>
                    Support emails are answered within 24 hours on weekdays. Abuse reports are prioritized and reviewed
                    within the same business day. Legal and press inquiries may take 2–3 business days.
                </p>
            </section>

            <section>
                <h2>Before you write</h2>
                <p>
                    For account and payment issues, please include your account email and a short description of what
                    went wrong. For content reports, include the URL or media ID if you have one.
                </p>
            </section>
        </LegalLayout>
    );
}
